import React, { useState, useEffect, useRef, Suspense } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { usePlan } from './contexts/PlanContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from './contexts/ToastContext';
import { slackService } from './services/slack';
import hubspotService from './services/hubspot';
import salesforceService from './services/salesforce';
import googleDocsService from './services/googleDocs';
import './Settings.css';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Backdrop,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import { FaPlus, FaExclamationTriangle, FaTrash, FaInfoCircle, FaExternalLinkAlt } from 'react-icons/fa';

// Import team service
import { getTeamMembers, inviteTeamMember, updateMemberRole, removeTeamMember } from './services/team';
import TeamMembersList from './components/TeamMembersList';
import TeamInviteDialog from './components/TeamInviteDialog';
import ModelSelector from './components/ModelSelector';

// Use relative URL for same-domain deployments (like Vercel)
// Use full URL only for cross-domain deployments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

const ROLES = ['Sales', 'Marketing', 'Product', 'Support', 'Other'];
const INDUSTRIES = ['Financial Services', 'Healthcare', 'Enterprise SaaS', 'Other'];

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function Settings() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Cache state
  const [cache, setCache] = useState({});
  
  // Track if HubSpot status has been checked to prevent repeated checks
  const hubspotStatusCheckedRef = useRef(false);
  
  // Granular loading states
  const [, setLoadingStates] = useState({
    profile: false,
    billing: false,
    team: false,
    integrations: false
  });
  
  const [activeSection, setActiveSection] = useState('account');
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    jobTitle: '',
    email: '',
    phone: '',
    profilePicture: null,
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    notifications: {
      email: true,
      inApp: true,
      push: false
    },
    role: ROLES[0], // 'Sales Rep'
    industry: INDUSTRIES[0],
  });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeletionOverlay, setShowDeletionOverlay] = useState(false);
  const [showPasswordSuccessOverlay, setShowPasswordSuccessOverlay] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [show2FAPasswordDialog, setShow2FAPasswordDialog] = useState(false);
  const [twoFactorPassword, setTwoFactorPassword] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [billingData, setBillingData] = useState({
    planName: 'Free',
    planType: 'Monthly',
    formattedPrice: '$0/month',
    formattedNextBilling: null,
    status: 'active',
    statusColor: '#22c55e'
  });
  const [billingLoading, setBillingLoading] = useState(false);
  // Invoices (Individual/Team): month filter as "YYYY-MM", list of invoices
  const [invoicesMonthFilter, setInvoicesMonthFilter] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [invoices, setInvoices] = useState([]);

  const [slackUserId, setSlackUserId] = useState('');
  const [slackLinkLoading, setSlackLinkLoading] = useState(false);
  const [slackLinkResult, setSlackLinkResult] = useState('');
  const [googleDocsConnected, setGoogleDocsConnected] = useState(false);
  const [googleDocsLoading, setGoogleDocsLoading] = useState(false);
  const [isSlackLinked, setIsSlackLinked] = useState(false);
  const [currentSlackUserId, setCurrentSlackUserId] = useState('');
  const [showSlackInfoDialog, setShowSlackInfoDialog] = useState(false);
  const [showGoogleDocsInfoDialog, setShowGoogleDocsInfoDialog] = useState(false);
  const [showHubspotInfoDialog, setShowHubspotInfoDialog] = useState(false);
  const [showSalesforceInfoDialog, setShowSalesforceInfoDialog] = useState(false);
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotPortalId, setHubspotPortalId] = useState('');
  const [salesforceConnected, setSalesforceConnected] = useState(false);
  const [salesforceLoading, setSalesforceLoading] = useState(false);
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [, setInviteData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    userRole: 'member'
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [, setCurrentUserRole] = useState('member');
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  
  // Enhanced team management state
  
  // Remove member dialog state
  const [removeMemberDialog, setRemoveMemberDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);
  
  // Get plan context at component level
  const { planType } = usePlan();
  const hasSlackAccess = ['Individual', 'Team'].includes(planType);
  const hasGoogleDocsAccess = ['Individual', 'Team'].includes(planType);

  useEffect(() => {
    let isActive = true;

    if (!hasGoogleDocsAccess) {
      setGoogleDocsConnected(false);
      return;
    }

    setGoogleDocsLoading(true);

    googleDocsService
      .getStatus()
      .then((connected) => {
        if (isActive) {
          setGoogleDocsConnected(!!connected);
        }
      })
      .catch((error) => {
        console.error('Error checking Google Docs status:', error);
      })
      .finally(() => {
        if (isActive) {
          setGoogleDocsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [hasGoogleDocsAccess]);
  const hasHubspotAccess = planType === 'Team';
  const hasSalesforceAccess = planType === 'Team';

  // Cache utility functions
  const getCachedData = (key) => {
    const cached = cache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  };

  const setCachedData = (key, data) => {
    setCache(prev => ({
      ...prev,
      [key]: { data, timestamp: Date.now() }
    }));
  };

  // const setLoadingState = (key, loading) => {
  //   setLoadingStates(prev => ({ ...prev, [key]: loading }));
  // };

  // Cache invalidation functions
  const invalidateCache = (key) => {
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[key];
      return newCache;
    });
  };

  // const invalidateAllCache = () => {
  //   setCache({});
  // };

  const loadSettings = async () => {
    setIsLoading(true);
    
    try {
      // Check cache first for critical data
      const cachedSettings = getCachedData('settingsData');
      if (cachedSettings) {
        setProfileData(cachedSettings.profile);
        setBillingData(cachedSettings.billing);
        // Load 2FA status from cache
        if (cachedSettings.user?.twoFactorEnabled !== undefined) {
          setTwoFactorEnabled(cachedSettings.user.twoFactorEnabled);
        }
        setIsLoading(false);
        
        // Load optional data in background
        loadOptionalData(cachedSettings.user);
        return;
      }
      
      // Use the new optimized endpoint that combines multiple data sources
      const settingsRes = await api.get('/users/settings-data');
      
      const { profile, user, billing } = settingsRes.data.data;
      
      // Cache the settings data
      setCachedData('settingsData', {
        profile,
        user,
        billing,
        timestamp: Date.now()
      });
      
      setProfileData(profile);
      setBillingData(billing);
      
      // Load 2FA status
      if (user.twoFactorEnabled !== undefined) {
        setTwoFactorEnabled(user.twoFactorEnabled);
      }
      
      // Show page with basic data
      setIsLoading(false);
      
      // Load optional data in background
      loadOptionalData(user);
      
    } catch (error) {
      console.error('Error loading settings:', error);
      
      // Fallback to individual API calls if the optimized endpoint fails
      try {
        const [profileRes, userRes] = await Promise.all([
          api.get('/users/profile'),
          api.get('/auth/me')
        ]);
        
        const profileData = profileRes.data.data;
        const userData = userRes.data.data.user;
        
        setProfileData(profileData);
        
        // Create basic billing data
        const planName = userData.currentPlanName || 'Free';
        const planType = userData.currentPlanType || 'Monthly';
        const price = planName === 'Free' ? 0 : 
                     planName === 'Individual' ? 20 : 48;
        
        const initialBillingData = {
          planName,
          planType,
          formattedPrice: planName === 'Free' ? '$0/month' : `$${price}/user/month`,
          formattedNextBilling: null,
          status: 'active',
          statusColor: '#22c55e'
        };
        
        setBillingData(initialBillingData);
        
        // Load 2FA status
        if (userData.twoFactorEnabled !== undefined) {
          setTwoFactorEnabled(userData.twoFactorEnabled);
        }
        
        setIsLoading(false);
        
        // Load optional data in background
        loadOptionalData(userData);
        
      } catch (fallbackError) {
        showError('Failed to load settings. Please try again.');
        setIsLoading(false);
      }
    }
  };

  // Simple useEffect to load settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Fetch invoices from API for Individual/Team (filter by selected month)
  useEffect(() => {
    const planName = billingData.planName;
    if (planName !== 'Individual' && planName !== 'Team') {
      setInvoices([]);
      return;
    }
    let cancelled = false;
    api.get('/payments/invoices', { params: { month: invoicesMonthFilter } })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data?.invoices || [];
        setInvoices(list);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      });
    return () => { cancelled = true; };
  }, [billingData.planName, invoicesMonthFilter]);

  // Check if user is returning from HubSpot OAuth
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hubspotParam = urlParams.get('hubspot');
    
    if (hubspotParam === 'success' && planType === 'Team') {
      // Fetch connection status to get portal ID
      hubspotService.getConnectionStatus()
        .then(response => {
          if (response.status === 'success' && response.data.connected) {
            setHubspotConnected(true);
            if (response.data.portalId) {
              setHubspotPortalId(response.data.portalId);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching HubSpot status after OAuth:', error);
          // Still set as connected even if we can't get portal ID
          setHubspotConnected(true);
        });
      
      // Clean up URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [planType]);

  const loadOptionalData = async (userData) => {
    try {
      const optionalCalls = [];
      
      // Only fetch subscription status if user has a paid plan
      if (userData.currentPlanName && userData.currentPlanName !== 'Free') {
        optionalCalls.push(
          api.get('/payments/subscription-status').catch(error => {
            console.log('Subscription status fetch failed, using fallback:', error);
            return null;
          })
        );
      }
      
      // Load Slack user status for eligible plans
      if (['Individual', 'Team'].includes(userData.currentPlanName)) {
        optionalCalls.push(
          slackService.getUserStatus().catch(error => {
            console.error('Error loading Slack status:', error);
            return null;
          })
        );
      }
      
      // Load HubSpot connection status for Team plan (only check once on initial load if not already connected)
      if (userData.currentPlanName === 'Team' && !hubspotStatusCheckedRef.current && !hubspotConnected) {
        hubspotStatusCheckedRef.current = true;
        optionalCalls.push(
          hubspotService.getConnectionStatus().catch(error => {
            console.error('Error loading HubSpot status:', error);
            return null;
          })
        );
      }
      
      // Execute optional calls in parallel
      if (optionalCalls.length > 0) {
        const results = await Promise.allSettled(optionalCalls);
        
        // Process subscription data
        const subscriptionRes = results[0];
        if (subscriptionRes?.status === 'fulfilled' && subscriptionRes.value) {
          const subscriptionData = subscriptionRes.value.data?.data || {};
          
          let formattedNextBilling = null;
          if (subscriptionData.nextBillingDate) {
            const nextBillingDate = new Date(subscriptionData.nextBillingDate);
            formattedNextBilling = nextBillingDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
          
          setBillingData(prev => ({
            ...prev,
            formattedNextBilling,
            status: subscriptionData.status || 'active',
            statusColor: subscriptionData.status === 'expired' ? '#ef4444' : 
                        subscriptionData.status === 'overdue' ? '#f59e0b' : '#22c55e'
          }));
        }
        
        // Process Slack data
        const slackRes = results[1];
        if (slackRes?.status === 'fulfilled' && slackRes.value) {
          if (slackRes.value.status === 'success') {
            setIsSlackLinked(slackRes.value.data.isLinked);
            setCurrentSlackUserId(slackRes.value.data.slackUserId || '');
          }
        }
        
        // Process HubSpot data
        const hubspotRes = results[2];
        if (hubspotRes?.status === 'fulfilled' && hubspotRes.value) {
          if (hubspotRes.value.status === 'success') {
            setHubspotConnected(hubspotRes.value.data.connected);
            if (hubspotRes.value.data.portalId) {
              setHubspotPortalId(hubspotRes.value.data.portalId);
            }
          }
        }
      }
      
      // Load team members for Team plan
      if (userData.currentPlanName === 'Team') {
        loadTeamMembers();
      }
      
    } catch (error) {
      console.error('Error loading optional data:', error);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    setSaveLoading(true);
    setSaveError('');
    setSaveSuccess('');
    
    try {
      const response = await api.patch('/users/me', profileData);
      
      if (response.data.status === 'success') {
        const successMessage = 'Profile updated successfully!';
        setSaveSuccess(successMessage);
        showSuccess(successMessage);
        
        // Invalidate cache to ensure fresh data
        invalidateCache('settingsData');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile. Please try again.';
      setSaveError(errorMessage);
      showError(errorMessage);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleToggle2FA = () => {
    setShow2FAPasswordDialog(true);
    setTwoFactorPassword('');
    setTwoFactorError('');
  };

  const handleClose2FAPasswordDialog = () => {
    setShow2FAPasswordDialog(false);
    setTwoFactorPassword('');
    setTwoFactorError('');
  };

  const handleEnable2FA = async () => {
    if (!twoFactorPassword) {
      setTwoFactorError('Password is required');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError('');

    try {
      const response = await api.post('/auth/enable-2fa', { password: twoFactorPassword });
      
      if (response.data.status === 'success') {
        setTwoFactorEnabled(true);
        showSuccess('Two-factor authentication has been enabled');
        handleClose2FAPasswordDialog();
        // Invalidate cache to ensure fresh data on next load
        invalidateCache('settingsData');
      }
    } catch (error) {
      console.error('Failed to enable 2FA:', error);
      const errorMessage = error.response?.data?.message || 'Failed to enable 2FA. Please check your password and try again.';
      setTwoFactorError(errorMessage);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFactorPassword) {
      setTwoFactorError('Password is required');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError('');

    try {
      const response = await api.post('/auth/disable-2fa', { password: twoFactorPassword });
      
      if (response.data.status === 'success') {
        setTwoFactorEnabled(false);
        showSuccess('Two-factor authentication has been disabled');
        handleClose2FAPasswordDialog();
        // Invalidate cache to ensure fresh data on next load
        invalidateCache('settingsData');
      }
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      const errorMessage = error.response?.data?.message || 'Failed to disable 2FA. Please check your password and try again.';
      setTwoFactorError(errorMessage);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    
    try {
      // Validate passwords
      if (!passwords.current || !passwords.new || !passwords.confirm) {
        const errorMsg = 'All password fields are required.';
        setPasswordError(errorMsg);
        showError(errorMsg);
        setPasswordLoading(false);
        return;
      }
      
      if (passwords.new !== passwords.confirm) {
        const errorMsg = 'New passwords do not match.';
        setPasswordError(errorMsg);
        showError(errorMsg);
        setPasswordLoading(false);
        return;
      }
      
      if (passwords.new.length < 8) {
        const errorMsg = 'New password must be at least 8 characters long.';
        setPasswordError(errorMsg);
        showError(errorMsg);
        setPasswordLoading(false);
        return;
      }
      
      // Make API call to update password
      const response = await api.post('/auth/change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.new
      });
      
      if (response.data.status === 'success') {
        const successMsg = 'Password updated successfully!';
        setPasswordSuccess(successMsg);
        showSuccess(successMsg);
        setPasswords({ current: '', new: '', confirm: '' });
        setShowPasswordDialog(false);
        setShowPasswordSuccessOverlay(true);
        
        // Show overlay for 5 seconds
        setTimeout(() => {
          setShowPasswordSuccessOverlay(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Password update error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update password. Please try again.';
      setPasswordError(errorMessage);
      showError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const response = await api.delete('/auth/delete-account');
      if (response.data.status === 'success') {
        setDeleteLoading(false);
        setShowDeleteDialog(false);
        setShowDeletionOverlay(true);
        
        // Show overlay for 7 seconds
        setTimeout(() => {
          setShowDeletionOverlay(false);
          // Logout and redirect to homepage
          logout();
          navigate('/');
        }, 7000);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteLoading(false);
      // You might want to show an error message to the user here
    }
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPasswords({ current: '', new: '', confirm: '' });
  };



  const handleCancelSubscription = async () => {
    setBillingLoading(true);
    try {
      const response = await api.post('/billing/cancel');
      
      if (response.data.status === 'success') {
        setBillingData(response.data.data.billing);
        showSuccess('Subscription cancelled successfully.');
        setShowCancelDialog(false); // Close dialog on success
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      showError('Failed to cancel subscription. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };
  
  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleSlackLink = async () => {
    if (!slackUserId.trim()) {
      setSlackLinkResult('Please enter your Slack user ID');
      return;
    }

    setSlackLinkLoading(true);
    setSlackLinkResult('');

    try {
      await slackService.linkUser(slackUserId);
      setIsSlackLinked(true);
      setCurrentSlackUserId(slackUserId);
      setSlackUserId('');
      setSlackLinkResult(''); // Clear any previous result messages
    } catch (error) {
      console.error('Slack link failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to link Slack account. Please try again.';
      setSlackLinkResult(`${errorMessage}`);
    } finally {
      setSlackLinkLoading(false);
    }
  };

  const handleSlackUnlink = async () => {
    setSlackLinkLoading(true);
    setSlackLinkResult('');

    try {
      await slackService.unlinkUser();
      setSlackLinkResult(' Slack account unlinked successfully!');
      setIsSlackLinked(false);
      setCurrentSlackUserId('');

      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setSlackLinkResult('');
      }, 3000); 
           
    } catch (error) {
      console.error('Slack unlink failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to unlink Slack account. Please try again.';
      setSlackLinkResult(`${errorMessage}`);
    } finally {
      setSlackLinkLoading(false);
    }
  };

  const handleGoogleDocsConnect = async () => {
    try {
      setGoogleDocsLoading(true);
      
      // Check if user is logged in
      if (!user || !user.id) {
        alert('Please log in to connect Google Docs.');
        return;
      }
      
      // Try with session-based auth first, fallback to userId parameter if session fails
      let response;
      try {
        response = await api.get('/google-docs/auth', {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (sessionError) {
        // If session-based auth fails, try with userId as query parameter
        if (sessionError.response?.status === 401) {
          console.warn('Session-based auth failed, trying with userId parameter');
          response = await api.get(`/google-docs/auth?userId=${user.id}`, {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            }
          });
        } else {
          throw sessionError;
        }
      }
      
      const authUrl = response.data?.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        console.error('Google Docs auth URL not received');
        alert('Failed to get Google Docs authorization URL. Please try again.');
      }
    } catch (error) {
      console.error('Failed to connect Google Docs:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // Handle 401 errors specifically
      if (error.response?.status === 401) {
        alert('Your session has expired. Please refresh the page and try again. If the problem persists, log out and log back in.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to connect Google Docs';
        alert(`Error: ${errorMessage}`);
      }
    } finally {
      setGoogleDocsLoading(false);
    }
  };

  const handleGoogleDocsDisconnect = async () => {
    try {
      setGoogleDocsLoading(true);
      const response = await googleDocsService.disconnect();
      if (response.status === 'success') {
        setGoogleDocsConnected(false);
      }
    } catch (error) {
      console.error('Failed to disconnect Google Docs:', error);
    } finally {
      setGoogleDocsLoading(false);
    }
  };

  const handleHubspotConnect = async () => {
    try {
      setHubspotLoading(true);
      console.log('Getting HubSpot auth URL...');
      
      // Add more detailed logging
      const authUrl = await hubspotService.getAuthUrl();
      console.log('HubSpot auth URL received:', authUrl);
      
      if (!authUrl) {
        console.error('HubSpot auth URL is undefined!');
        return;
      }
      
      console.log('Redirecting to:', authUrl);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to connect HubSpot:', error);
      console.error('Error details:', error.response || error);
    } finally {
      setHubspotLoading(false);
    }
  };

  const handleHubspotDisconnect = async () => {
    setHubspotLoading(true);
    try {
      await hubspotService.disconnect();
      setHubspotConnected(false);
      setHubspotPortalId('');
      // Reset the ref so status can be checked again if user reconnects
      hubspotStatusCheckedRef.current = false;
    } catch (error) {
      console.error('Error disconnecting HubSpot:', error);
    } finally {
      setHubspotLoading(false);
    }
  };

  const handleSalesforceConnect = async () => {
    try {
      setSalesforceLoading(true);
      const authUrl = await salesforceService.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to connect Salesforce:', error);
      setSalesforceLoading(false);
    }
  };

  const handleSalesforceDisconnect = async () => {
    try {
      setSalesforceLoading(true);
      await salesforceService.disconnect();
      setSalesforceConnected(false);
    } catch (error) {
      console.error('Error disconnecting Salesforce:', error);
    } finally {
      setSalesforceLoading(false);
    }
  };

  // Team management functions
  const loadTeamMembers = async () => {
    if (planType !== 'Team') {
      return;
    }
    
    // Check cache first
    const cachedTeamMembers = getCachedData('teamMembers');
    if (cachedTeamMembers) {
      setTeamMembers(cachedTeamMembers.members || []);
      setCurrentUserRole(cachedTeamMembers.currentUser?.userRole || 'member');
      setIsCompanyAdmin(cachedTeamMembers.currentUser?.isCompanyAdmin || false);
      return;
    }
    
    setTeamLoading(true);
    try {
      const response = await getTeamMembers();
      
      // Fix the data access to use the correct nested structure
      const teamData = response.data.data || response.data;
      const members = teamData.members || [];
      const currentUser = teamData.currentUser || {};
      
      // Cache the team data
      setCachedData('teamMembers', {
        members,
        currentUser,
        timestamp: Date.now()
      });
      
      setTeamMembers(members);
      setCurrentUserRole(currentUser.userRole || 'member');
      setIsCompanyAdmin(currentUser.isCompanyAdmin || false);
    } catch (error) {
      console.error('Error loading team members:', error);
      showError('Failed to load team members');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleInviteMember = async (formData) => {
    setInviteLoading(true);
    setTeamError('');
    setInviteSuccess('');
    
    try {
      await inviteTeamMember(formData);
      
      // Show success message with details
      const successMessage = `The Team Invitation email has been sent to ${formData.firstName} ${formData.lastName} (${formData.email}). They will receive a secure link to join your team.`;
      
      setInviteSuccess(successMessage);
      showSuccess(successMessage);
      
      // Close dialog and reload team members
      setShowInviteDialog(false);
      setInviteData({ email: '', firstName: '', lastName: '', userRole: 'member' });
      loadTeamMembers();
      
    } catch (error) {
      console.error('Error inviting member:', error);
      const errorMsg = error.message || 'Failed to invite team member';
      setTeamError(errorMsg);
      showError(errorMsg);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = (member) => {
    setMemberToRemove(member);
    setRemoveMemberDialog(true);
  };

  const handleRemoveMemberConfirm = async () => {
    if (!memberToRemove) return;
    
    setRemovingMember(true);
    try {
      await removeTeamMember(memberToRemove.id);
      await loadTeamMembers();
      
      // Close dialog and reset state
      setRemoveMemberDialog(false);
      setMemberToRemove(null);
      
    } catch (error) {
      console.error('Error removing member:', error);
      showError(error.message || 'Failed to remove team member');
    } finally {
      setRemovingMember(false);
    }
  };

  const handleRemoveMemberCancel = () => {
    setRemoveMemberDialog(false);
    setMemberToRemove(null);
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      await updateMemberRole(memberId, newRole);
      await loadTeamMembers();
    } catch (error) {
      console.error('Error updating member role:', error);
      showError(error.message || 'Failed to update member role');
    }
  };


  // Header bar (outside card)
  const renderHeader = () => (
    <div className="settings-header-row" style={{ background: 'linear-gradient(120deg, rgba(241, 245, 249, 1) 3%, rgba(219, 234, 254, 1) 37%, rgba(237, 233, 254, 1) 100%)', height: '9vh', padding: '8px 0px 8px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', marginBottom: 16, gap: 4, alignItems: 'center', display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', borderBottom: '1px solid var(--border-color)' }}>
          <div className="settings-header-left">
            <div style={{ fontSize: 24, fontWeight: 600 }}>Settings</div>
          </div>
    </div>
  );

  // Tab bar (outside card)
  const renderTabs = () => {
    return (
      <div className="Tabs" style={{ display: 'flex', justifyContent: 'left', marginTop: 4, marginBottom: 8, marginLeft: 10 }}>
        <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 2px 8px rgba(35, 62, 128, 0.04)', display: 'flex', p: 0.1, width: 'fit-content', gap: 0.5 }}>
          <Button
            onClick={() => setActiveSection('account')}
            sx={{
              minWidth: 100,
              background: activeSection === 'account' ? '#f3f4f6' : 'transparent',
              color: '#111827',
              fontWeight: 600,
              fontSize: 11,
              borderRadius: 1,
              minHeight: 28,
              py: 0.3,
              px: 2,
            }}
          >
            Profile
          </Button>
          <Button
            onClick={() => setActiveSection('billing')}
            sx={{
              minWidth: 100,
              background: activeSection === 'billing' ? '#f3f4f6' : 'transparent',
              color: '#111827',
              fontWeight: 600,
              fontSize: 11,
              borderRadius: 1,
              minHeight: 28,
              py: 0.3,
              px: 2,
            }}
          >
            Billing
          </Button>
          <Button
            onClick={() => setActiveSection('integrations')}
            sx={{
              minWidth: 120,
              background: activeSection === 'integrations' ? '#f3f4f6' : 'transparent',
              color: '#111827',
              fontWeight: 600,
              fontSize: 11,
              borderRadius: 1,
              minHeight: 28,
              py: 0.3,
              px: 2,
            }}
          >
            Integrations
          </Button>
          {planType === 'Team' && (
            <Button
              onClick={() => setActiveSection('team')}
              sx={{
                minWidth: 80,
                background: activeSection === 'team' ? '#f3f4f6' : 'transparent',
                color: '#111827',
                fontWeight: 600,
                fontSize: 11,
                borderRadius: 1,
                minHeight: 28,
                py: 0.3,
                px: 2,
              }}
            >
              Team
            </Button>
          )}
        </Box>
      </div>
    );
  };

  // Profile Section (inside card)
  const renderAccountSection = () => (
    <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: 'none', p: 0, mb: 1, width: '100%' }}>
      <Box sx={{ 
        background: '#fcfcfd', 
        borderRadius: 1, 
        boxShadow: '0 2px 8px rgba(35, 62, 128, 0.04)', 
        p: 1.5,
        height: 'calc(100vh - 250px)', // Adjusted for compact header and tabs
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
          Profile Information
        </Typography>
        <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
          Update your personal details and preferences
        </Typography>
        
        {/* Scrollable content container */}
        <Box sx={{ 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1, // Add padding for scrollbar
          pb: 2, // Add bottom padding to prevent content cutoff
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
        }}>
          {/* Profile Info Card */}
          <Box className="profile-box" sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', p: 2, mb: 2, fontSize: 11 }}>
            <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
              Personal Details
            </Typography>
            <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
              Update your personal information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="First Name" value={profileData.firstName} onChange={e => handleProfileChange('firstName', e.target.value)} fullWidth margin="dense" size="small" InputLabelProps={{ style: { fontSize: 12 } }} inputProps={{ style: { fontSize: 12 } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Last Name" value={profileData.lastName} onChange={e => handleProfileChange('lastName', e.target.value)} fullWidth margin="dense" size="small" InputLabelProps={{ style: { fontSize: 12 } }} inputProps={{ style: { fontSize: 12 } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Email Address" value={profileData.email} onChange={e => handleProfileChange('email', e.target.value)} fullWidth margin="dense" size="small" InputLabelProps={{ style: { fontSize: 12 } }} inputProps={{ style: { fontSize: 12 } }} disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Your Company" value={profileData.companyName} onChange={e => handleProfileChange('companyName', e.target.value)} fullWidth margin="dense" size="small" InputLabelProps={{ style: { fontSize: 12 } }} inputProps={{ style: { fontSize: 12 } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel style={{ fontSize: 12 }}>Your Role</InputLabel>
                  <Select value={profileData.role} label="Your Role" onChange={e => handleProfileChange('role', e.target.value)} sx={{ fontSize: 12 }} inputProps={{ style: { fontSize: 12 } }}>
                    {ROLES.map((role) => (
                      <MenuItem key={role} value={role} style={{ fontSize: 12 }}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel style={{ fontSize: 12 }}>Industry Focus</InputLabel>
                  <Select value={profileData.industry} label="Industry Focus" onChange={e => handleProfileChange('industry', e.target.value)} sx={{ fontSize: 12 }} inputProps={{ style: { fontSize: 12 } }}>
                    {INDUSTRIES.map((ind) => (
                      <MenuItem key={ind} value={ind} style={{ fontSize: 12 }}>{ind}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {/* Success and Error Messages */}
            {saveSuccess && (
              <Alert severity="success" sx={{ mt: 2, fontSize: 12 }}>
                {saveSuccess}
              </Alert>
            )}
            {saveError && (
              <Alert severity="error" sx={{ mt: 2, fontSize: 12 }}>
                {saveError}
              </Alert>
            )}
            
            <Box mt={1}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSaveSettings} 
                disabled={saveLoading}
                sx={{ fontWeight: 600, fontSize: 12, px: 1.5, py: 0.3, minHeight: 28 }}
              >
                {saveLoading ? <CircularProgress size={16} /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
          
          {/* AI Model Preferences Card - Team Plan Only */}
          {planType === 'Team' && (
            <Box className="model-preferences-box" sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', p: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
                AI Model Preferences
              </Typography>
              <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
                Choose your preferred AI models for different tasks
              </Typography>
              <ModelSelector />
            </Box>
          )}
          
          {/* Security Card */}
          <Box className="security-box" sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', p: 2 }}>
            <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
              Security
            </Typography>
            <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
              Manage your account security settings
            </Typography>
            
            {/* Two-Factor Authentication */}
            <Box sx={{ mb: 2, p: 1.5, background: '#f8fafc', borderRadius: 1, border: '1px solid #e1e5e9' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#111827" fontSize={12}>
                    Two-Factor Authentication
                  </Typography>
                  <Typography variant="body2" color="#64748b" fontSize={10}>
                    {twoFactorEnabled ? 'Enabled - Extra security for your account' : 'Add an extra layer of security'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color={twoFactorEnabled ? '#22c55e' : '#64748b'} fontSize={11} fontWeight={600}>
                    {twoFactorEnabled ? 'ON' : 'OFF'}
                  </Typography>
                  <Switch
                    checked={twoFactorEnabled}
                    onChange={handleToggle2FA}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#22c55e',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#22c55e',
                      },
                    }}
                  />
                </Box>
              </Box>
              {twoFactorEnabled && (
                <Box sx={{ mt: 1, p: 1, background: '#f0fdf4', borderRadius: 0.5, border: '1px solid #bbf7d0' }}>
                  <Typography variant="body2" color="#166534" fontSize={10}>
                    ✓ 2FA is active. You'll receive a verification code via email when logging in.
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box mb={0.5}>
              <Button variant="outlined" fullWidth sx={{ mb: 0.5, marginBottom: 2, fontWeight: 500, fontSize: 12, py: 0.3, minHeight: 28 }} onClick={() => setShowPasswordDialog(true)}>
                Change Password
              </Button>
              <Button variant="outlined" color="error" fullWidth sx={{ fontWeight: 500, fontSize: 12, py: 0.3, minHeight: 28 }} onClick={() => setShowDeleteDialog(true)}>
                Delete Account
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Billing Section (inside card)
  const renderBillingSection = () => (
    <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: 'none', p: 0, mb: 1, width: '100%' }}>
      <Box sx={{ 
        background: '#fcfcfd', 
        borderRadius: 1, 
        boxShadow: '0 2px 8px rgba(35, 62, 128, 0.04)', 
        p: 1.5,
        height: 'calc(100vh - 150px)', // Adjusted for compact header and tabs
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
          Billing & Subscription
        </Typography>
        <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
          Manage your subscription and billing details
        </Typography>
        
        {/* Scrollable content container */}
        <Box sx={{ 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1, // Add padding for scrollbar
          pb: 2, // Add bottom padding to prevent content cutoff
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
        }}>
          <Box className="billing-box" sx={{ background: 'linear-gradient(90deg, #f3f6fd 0%, #f7f9fd 100%)', borderRadius: 1, p: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <Typography fontWeight={700} color="#111827" fontSize={12}>
                {billingData.planName} Plan
              </Typography>
              <Typography color="#64748b" fontSize={11}>
                {billingData.formattedPrice} • {billingData.planType}
                {billingData.status === 'cancelled' && ' • Cancelled'}
              </Typography>
            </Box>
            <Box>
              <span style={{ 
                background: billingData.statusColor, 
                color: '#fff', 
                borderRadius: 8, 
                padding: '1.5px 7px', 
                fontWeight: 600, 
                fontSize: 10,
                textTransform: 'capitalize'
              }}>
                {billingData.status}
              </span>
            </Box>
            </Box>
            
            {/* Team Plan Information */}
            {billingData.planName === 'Team' && billingData.isTeamPlan && (
              <Box sx={{ mt: 1, p: 1, background: 'rgba(59, 158, 252, 0.1)', borderRadius: 1, border: '1px solid rgba(59, 158, 252, 0.2)' }}>
                <Typography color="#233e80" fontSize={11} sx={{ fontWeight: 600, mb: 0.5 }}>
                  Team Plan Details
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography color="#64748b" fontSize={10}>
                    Current Team Size:
                  </Typography>
                  <Typography color="#111827" fontSize={10} sx={{ fontWeight: 600 }}>
                    {billingData.teamSize || 1} users
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography color="#64748b" fontSize={10}>
                    Plan Limit:
                  </Typography>
                  <Typography color="#111827" fontSize={10} sx={{ fontWeight: 600 }}>
                    Up to {billingData.teamSize <= 5 ? 5 : billingData.teamSize <= 10 ? 10 : 15} users
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography color="#64748b" fontSize={10}>
                    Pricing Slab:
                  </Typography>
                  <Typography color="#111827" fontSize={10} sx={{ fontWeight: 600 }}>
                    {billingData.teamSize <= 5 ? 'Slab 1' : billingData.teamSize <= 10 ? 'Slab 2' : 'Slab 3'}
                  </Typography>
                </Box>
                {billingData.teamSize >= 15 && (
                  <Box sx={{ mt: 0.5, p: 0.5, background: '#fef2f2', borderRadius: 0.5, border: '1px solid #fecaca' }}>
                    <Typography color="#dc2626" fontSize={9} sx={{ fontWeight: 500 }}>
                      ⚠️ Maximum team size reached. Contact sales for larger teams.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Next Billing Date */}
            {billingData.formattedNextBilling && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography color="#64748b" fontSize={11} sx={{ fontWeight: 500 }}>
                  Next billing:
                </Typography>
                <Typography color="#111827" fontSize={11} sx={{ fontWeight: 600 }}>
                  {billingData.formattedNextBilling}
                </Typography>
              </Box>
            )}
            
            {/* Subscription Status Warnings */}
            {billingData.status === 'expired' && (
              <Box sx={{ mt: 1, p: 1, background: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                <Typography color="#dc2626" fontSize={10} sx={{ fontWeight: 500 }}>
                  ⚠️ Your subscription has expired. Please renew to continue accessing premium features.
                </Typography>
              </Box>
            )}
            
            {billingData.status === 'overdue' && (
              <Box sx={{ mt: 1, p: 1, background: '#fffbeb', borderRadius: 1, border: '1px solid #fed7aa' }}>
                <Typography color="#d97706" fontSize={10} sx={{ fontWeight: 500 }}>
                  ⚠️ Payment is overdue. Please update your payment method to avoid service interruption.
                </Typography>
              </Box>
            )}
            
            {billingData.status === 'renewing_soon' && (
              <Box sx={{ mt: 1, p: 1, background: '#f0f9ff', borderRadius: 1, border: '1px solid #bae6fd' }}>
                <Typography color="#0284c7" fontSize={10} sx={{ fontWeight: 500 }}>
                  ℹ️ Your subscription will renew automatically on {billingData.formattedNextBilling}.
                </Typography>
              </Box>
            )}
          </Box>

          <Grid container spacing={0.5}>
            <Grid item xs={12} md={6}>
              <Button 
                variant="outlined" 
                fullWidth 
                disabled={billingLoading || billingData.status === 'cancelled' || billingData.planName === 'Team'}
                onClick={() => navigate('/billing')}
                sx={{ fontWeight: 500, fontSize: 12, py: 0.3, minHeight: 28 }}
              >
                {billingLoading ? <CircularProgress size={16} /> : 
                 billingData.planName === 'Free' ? 'Upgrade Plan' : 
                 billingData.planName === 'Individual' ? 'Upgrade to Team' : 'Top Plan'}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Tooltip
                title={
                  billingData.status === 'cancelled' && billingData.formattedNextBilling
                    ? `Your subscription is already set to cancel at the end of your current billing period (${billingData.formattedNextBilling}).`
                    : ''
                }
              >
                <span style={{ display: 'inline-block', width: '100%' }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={billingLoading || billingData.status === 'cancelled' || billingData.planName === 'Free'}
                    onClick={handleCancelClick}
                    sx={{ fontWeight: 500, fontSize: 12, py: 0.3, minHeight: 28 }}
                  >
                    {billingLoading ? <CircularProgress size={16} /> : 'Cancel Subscription'}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>  

          {/* Invoices table - Individual and Team plans only */}
          {(billingData.planName === 'Individual' || billingData.planName === 'Team') && (
            <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', p: 2, mb: 1, mt: 10 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography fontWeight={700} color="#111827" fontSize={14}>
                  Invoices
                </Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={invoicesMonthFilter}
                    onChange={(e) => setInvoicesMonthFilter(e.target.value)}
                    displayEmpty
                    sx={{ fontSize: 12, height: 32, borderRadius: 1, borderColor: '#d1d5db' }}
                  >
                    {(() => {
                      const options = [];
                      const now = new Date();
                      for (let i = 0; i < 24; i++) {
                        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        options.push(<MenuItem key={value} value={value} sx={{ fontSize: 12 }}>{label}</MenuItem>);
                      }
                      return options;
                    })()}
                  </Select>
                </FormControl>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#374151', py: 1 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#374151', py: 1 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#374151', py: 1 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#374151', py: 1 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#374151', py: 1 }}>Invoice</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ fontSize: 12, color: '#64748b', py: 2 }}>
                          No invoices for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map((inv) => (
                        <TableRow key={inv.id} hover>
                          <TableCell sx={{ fontSize: 12, color: '#111827' }}>{inv.date}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#64748b' }}>{inv.description || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            <span style={{
                              background: inv.status === 'Paid' ? '#dcfce7' : inv.status === 'Pending' ? '#fef3c7' : '#f3f4f6',
                              color: inv.status === 'Paid' ? '#166534' : inv.status === 'Pending' ? '#92400e' : '#4b5563',
                              borderRadius: 6,
                              padding: '2px 8px',
                              fontWeight: 600,
                              fontSize: 10,
                              textTransform: 'capitalize'
                            }}>
                              {inv.status}
                            </span>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#111827' }}>{inv.amount}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {inv.invoiceUrl ? (
                              <Button
                                component="a"
                                href={inv.invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                sx={{ fontSize: 12, fontWeight: 500, color: '#2563eb', minWidth: 'auto', px: 0 }}
                                startIcon={<FaExternalLinkAlt size={12} />}
                              >
                                View
                              </Button>
                            ) : inv.receiptId ? (
                              <Button
                                size="small"
                                sx={{ fontSize: 12, fontWeight: 500, color: '#2563eb', minWidth: 'auto', px: 0 }}
                                startIcon={<FaExternalLinkAlt size={12} />}
                                onClick={async () => {
                                  try {
                                    const r = await api.get(`/payments/invoices/${inv.receiptId}/receipt-url`);
                                    const url = r.data?.data?.url;
                                    if (url) window.open(url, '_blank');
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}
                              >
                                View
                              </Button>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: 12 }}>View</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}


        </Box>
      </Box>
    </Box>
  );

  // Integrations Section (inside card)
  const renderIntegrationsSection = () => {

    return (
      <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: 'none', p: 0, mb: 1, width: '100%' }}>
        <Box sx={{ 
          background: '#fcfcfd', 
          borderRadius: 1, 
          boxShadow: '0 2px 8px rgba(35, 62, 128, 0.04)', 
          p: 1.5,
          height: 'calc(100vh - 250px)', // Increased space for header, tabs, and margins
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
            Integrations
          </Typography>
          <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
            Connect your Velox AI account with third-party services
          </Typography>

          {/* Scrollable content container */}
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1, // Add padding for scrollbar
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}>
            <Grid container spacing={2}>
              {/* Slack Integration */}
              <Grid item xs={12} md={4}>
                <Box sx={{ background: 'linear-gradient(90deg, #f3f6fd 0%, #f7f9fd 100%)', borderRadius: 1, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography fontWeight={700} color="#111827" fontSize={12}>
                          Slack Integration
                          <Button
                            size="small"
                            onClick={() => setShowSlackInfoDialog(true)}
                            sx={{ 
                              minWidth: 'auto', 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              p: 0
                            }}
                          >
                            <FaInfoCircle size={12} color="#1976d2" />
                          </Button>
                        </Typography>
                        <Typography color="#64748b" fontSize={11}>
                          Search and view your documents directly in Slack
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <span style={{ 
                        background: hasSlackAccess ? '#22c55e' : '#6b7280', 
                        color: '#fff', 
                        borderRadius: 8, 
                        padding: '1.5px 7px', 
                        fontWeight: 600, 
                        fontSize: 10,
                        textTransform: 'capitalize'
                      }}>
                        {hasSlackAccess ? 'Available' : 'Upgrade Required'}
                      </span>
                    </Box>
                  </Box>

                  {hasSlackAccess ? (
                    <Box>
                      {/* Slack Account Linking */}
                      <Typography color="#64748b" fontSize={11} sx={{ fontWeight: 500, mb: 1 }}>
                        Link your Slack account:
                      </Typography>
                      
                      {isSlackLinked ? (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ 
                            mb: 2, 
                            p: 1.5, 
                            background: '#f0fdf4', 
                            borderRadius: 1, 
                            border: '1px solid #bbf7d0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography 
                                color="#166534" 
                                fontSize={11} 
                                sx={{ fontWeight: 600 }}
                              >
                                 Your Slack account is linked (ID: {currentSlackUserId})
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleSlackUnlink}
                            disabled={slackLinkLoading}
                            sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28 }}
                          >
                            {slackLinkLoading ? <CircularProgress size={16} /> : 'Unlink Account'}
                          </Button>
                        </Box>
                      ) : (
                        <Grid container spacing={1} sx={{ mb: 2, alignContent: 'center' }}>
                          <Grid item xs={12} md={8}>
                            <TextField
                              label="Slack User ID (e.g., U1234567890)"
                              value={slackUserId}
                              onChange={(e) => setSlackUserId(e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="U1234567890"
                              InputLabelProps={{ style: { fontSize: 12 } }}
                              maxHeight={30}
                              inputProps={{ style: { fontSize: 12 } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Button
                              variant="contained"
                              onClick={handleSlackLink}
                              disabled={slackLinkLoading || !slackUserId.trim()}
                              fullWidth
                              sx={{ fontWeight: 600, fontSize: 10, maxHeight: 30 }}
                            >
                              {slackLinkLoading ? <CircularProgress size={16} /> : 'Link Account'}
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      {slackLinkResult && (
                        <Alert severity={slackLinkResult.includes('') ? 'success' : 'error'} sx={{ mb: 2, fontSize: 12 }}>
                          {slackLinkResult}
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, background: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                      <Typography color="#dc2626" fontSize={10} sx={{ fontWeight: 500 }}>
                        ⚠️ Slack integration requires Individual or Team plan. Upgrade to access this feature.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/billing')}
                        sx={{ mt: 1, fontWeight: 500, fontSize: 10, py: 0.2, minHeight: 24 }}
                      >
                        Upgrade Plan
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Google Docs Integration */}
              <Grid item xs={12} md={4}>
                <Box sx={{ background: 'linear-gradient(90deg, #f3f6fd 0%, #f7f9fd 100%)', borderRadius: 1, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography fontWeight={700} color="#111827" fontSize={12}>
                          Google Docs Integration
                        <Button
                          size="small"
                          onClick={() => setShowGoogleDocsInfoDialog(true)}
                          sx={{ 
                            minWidth: 'auto', 
                            width: 20, 
                            height: 20, 
                            borderRadius: '50%', 
                            p: 0
                          }}
                        >
                        <FaInfoCircle size={12} color="#1976d2" />
                        </Button>
                        </Typography>
                        <Typography color="#64748b" fontSize={11}>
                          Export your Velox AI documents directly to Google Docs
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {hasGoogleDocsAccess ? (
                    <Box>
                      {!googleDocsConnected ? (
                        <Box
                          variant="contained"
                          onClick={handleGoogleDocsConnect}
                          disabled={googleDocsLoading}
                          fullWidth
                          sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28, mb: 1 }}
                        >
                          {googleDocsLoading ? <CircularProgress size={16} /> : 'Coming Soon'}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="success" sx={{ mb: 1, fontSize: 12 }}>
                             Connected to Google Docs
                          </Alert>
                          <Button
                            variant="outlined"
                            onClick={handleGoogleDocsDisconnect}
                            disabled={googleDocsLoading}
                            fullWidth
                            sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28 }}
                          >
                            {googleDocsLoading ? <CircularProgress size={16} /> : 'Disconnect Google Docs'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, background: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                      <Typography color="#dc2626" fontSize={10} sx={{ fontWeight: 500 }}>
                        ⚠️ Google Docs integration requires Individual or Team plan. Upgrade to access this feature.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/billing')}
                        sx={{ mt: 1, fontWeight: 500, fontSize: 10, py: 0.2, minHeight: 24 }}
                      >
                        Upgrade Plan
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* HubSpot Integration */}
              <Grid item xs={12} md={4}>
                <Box sx={{ background: 'linear-gradient(90deg, #f3f6fd 0%, #f7f9fd 100%)', borderRadius: 1, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography fontWeight={700} color="#111827" fontSize={12}>
                          HubSpot CRM Integration
                      <Button
                        size="small"
                        onClick={() => setShowHubspotInfoDialog(true)}
                        sx={{ 
                          minWidth: 'auto', 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          p: 0
                        }}
                      >
                          <FaInfoCircle size={12} color="#1976d2" />
                        </Button>
                        </Typography>
                        <Typography color="#64748b" fontSize={11}>
                          Import CRM data from HubSpot
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ 
                        background: hasHubspotAccess ? '#22c55e' : '#6b7280', 
                        color: '#fff', 
                        borderRadius: 8, 
                        padding: '1.5px 7px', 
                        fontWeight: 600, 
                        fontSize: 10,
                        textTransform: 'capitalize'
                      }}>
                        {hasHubspotAccess ? 'Available' : 'Upgrade Required'}
                      </span>
                    </Box>
                  </Box>

                  {hasHubspotAccess ? (
                    <Box>
                      {/* Connection Status Display */}
                      {hubspotConnected && (
                        <Box sx={{ 
                          mb: 2, 
                          p: 1.5, 
                          background: '#f0fdf4', 
                          borderRadius: 1, 
                          border: '1px solid #bbf7d0',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography 
                              color="#166534" 
                              fontSize={11} 
                              sx={{ fontWeight: 600 }}
                            >
                               Connected to HubSpot{hubspotPortalId ? ` (Portal ID: ${hubspotPortalId})` : ''}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {!hubspotConnected ? (
                        <Button
                          variant="contained"
                          onClick={handleHubspotConnect}
                          disabled={hubspotLoading}
                          fullWidth
                          sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28, mb: 1 }}
                        >
                          {hubspotLoading ? <CircularProgress size={16} /> : 'Connect HubSpot'}
                        </Button>
                      ) : (
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={handleHubspotDisconnect}
                            disabled={hubspotLoading}
                            fullWidth
                            sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28, mb: 1 }}
                          >
                            {hubspotLoading ? <CircularProgress size={16} /> : 'Disconnect HubSpot'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, background: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                      <Typography color="#dc2626" fontSize={10} sx={{ fontWeight: 500 }}>
                        ⚠️ HubSpot integration requires Team plan. Upgrade to access this feature.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/billing')}
                        sx={{ mt: 1, fontWeight: 500, fontSize: 10, py: 0.2, minHeight: 24 }}
                      >
                        Upgrade Plan
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Salesforce Integration */}
              <Grid item xs={12} md={4}>
                <Box sx={{ background: 'linear-gradient(90deg, #f3f6fd 0%, #f7f9fd 100%)', borderRadius: 1, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography fontWeight={700} color="#111827" fontSize={12}>
                          Salesforce CRM Integration
                          <Button
                            size="small"
                            onClick={() => setShowSalesforceInfoDialog(true)}
                            sx={{ 
                              minWidth: 'auto', 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              p: 0
                            }}
                          >
                            <FaInfoCircle size={12} color="#1976d2" />
                          </Button>
                        </Typography>
                        <Typography color="#64748b" fontSize={11}>
                          Import CRM data from Salesforce
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {hasSalesforceAccess ? (
                    <Box>
                      {!salesforceConnected ? (
                        <Box
                          variant="contained"
                          onClick={handleSalesforceConnect}
                          disabled={salesforceLoading}
                          fullWidth
                          sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28, mb: 1 }}
                        >
                          {salesforceLoading ? <CircularProgress size={16} /> : 'Coming Soon'}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="success" sx={{ mb: 1, fontSize: 12 }}>
                             Connected to Salesforce
                          </Alert>
                          <Box
                            variant="outlined"
                            onClick={handleSalesforceDisconnect}
                            disabled={salesforceLoading}
                            fullWidth
                            sx={{ fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28 }}
                          >
                            {salesforceLoading ? <CircularProgress size={16} /> : 'Disconnect Salesforce'}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, background: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                      <Typography color="#dc2626" fontSize={10} sx={{ fontWeight: 500 }}>
                        ⚠️ Salesforce integration requires Team plan. Upgrade to access this feature.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/billing')}
                        sx={{ mt: 1, fontWeight: 500, fontSize: 10, py: 0.2, minHeight: 24 }}
                      >
                        Upgrade Plan
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  };

  // Team Management Section (Team plan only)
  const renderTeamSection = () => {
    if (planType !== 'Team') {
      return (
        <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: 'none', p: 0, mb: 1, width: '100%' }}>
          <Box sx={{ 
            background: '#fcfcfd', 
            borderRadius: 1, 
            boxShadow: '0 2px 8px rgba(35, 62, 128, 0.04)', 
            p: 1.5,
            height: 'calc(100vh - 200px)', // Increased space for header, tabs, and margins
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
              Team Management
            </Typography>
            <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
              This feature is only available for Team plan users. Current plan: {planType}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ background: '#fff', borderRadius: 1, boxShadow: 'none', p: 0, mb: 1, width: '100%' }}>
        <Box sx={{ 
          background: '#fcfcfd', 
          borderRadius: 1, 
          boxShadow: '0 2px 8px rgba(35, 62, 128, 0.04)', 
          p: 1.5,
          height: 'calc(100vh - 250px)', // Increased space for header, tabs, and margins
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Typography variant="h6" fontWeight={700} mb={0.5} color="#111827" fontSize={14}>
            Team Management
          </Typography>
          <Typography variant="body2" color="#64748b" mb={1} fontSize={11}>
            Manage your team members and their roles
          </Typography>

          {/* Scrollable content container */}
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1, // Add padding for scrollbar
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}>
            {isCompanyAdmin && (
              <Button
                variant="contained"
                onClick={() => setShowInviteDialog(true)}
                startIcon={<FaPlus />}
                sx={{ mb: 2, fontWeight: 600, fontSize: 12, py: 0.3, minHeight: 28 }}
              >
                Invite Team Member
              </Button>
            )}

            <TeamMembersList 
              members={teamMembers}
              currentUser={user}
              loading={teamLoading}
              isCompanyAdmin={isCompanyAdmin}
              onRemoveMember={handleRemoveMember}
              onUpdateRole={handleUpdateMemberRole}
            />
          </Box>
        </Box>
      </Box>
    );
  };


  return (
    <div style={{ className: 'settings-layout', background: '#F7F9FC', minHeight: 'calc(100vh - 10px)', width: '100%', borderRadius: '10px 0px 0px 0px', marginTop: '10px', overflowY: 'auto', display: 'flex', flexDirection: 'column'}}>
      {renderHeader()}
      {renderTabs()}
      
      {/* Loading State */}
      {isLoading && (
        <Box className="settings-container" sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 2px 8px rgba(35, 62, 128, 0.08)', width: '98%', marginLeft: 1, mb: 2, p: 4, textAlign: 'center', flex: 1 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading your settings...
          </Typography>
        </Box>
      )}
      
      {/* Content */}
      {!isLoading && (
        <Box className="settings-container" sx={{ background: '#fff', borderRadius: 1, boxShadow: '0 2px 8px rgba(35, 62, 128, 0.08)', width: '98%', marginLeft: 1, mb: 2, flex: 1, overflow: 'hidden' }}>
          {activeSection === 'account' && renderAccountSection()}
          {activeSection === 'billing' && renderBillingSection()}
          {activeSection === 'integrations' && renderIntegrationsSection()}
          {activeSection === 'team' && renderTeamSection()}
        </Box>
      )}
      {/* Password Reset Dialog */}
      {user?.provider === 'google' ? (
        <Dialog open={showPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Password Change Not Available</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ fontSize: 16, opacity: 0.9 }}>
              Your account with us is linked to your Google account. Your password is managed by Google.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePasswordDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog open={showPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <TextField
              label="Current password"
              type="password"
              fullWidth
              margin="normal"
              value={passwords.current}
              onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))}
            />
            <TextField
              label="New password"
              type="password"
              fullWidth
              margin="normal"
              value={passwords.new}
              onChange={e => setPasswords(p => ({ ...p, new: e.target.value }))}
            />
            <TextField
              label="Confirm password"
              type="password"
              fullWidth
              margin="normal"
              value={passwords.confirm}
              onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))}
            />
            {passwordError && <Alert severity="error" sx={{ mt: 2 }}>{passwordError}</Alert>}
            {passwordSuccess && <Alert severity="success" sx={{ mt: 2 }}>{passwordSuccess}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePasswordDialog} disabled={passwordLoading}>Cancel</Button>
            <Button onClick={handlePasswordReset} variant="contained" disabled={passwordLoading || !passwords.current || !passwords.new || !passwords.confirm}>
              {passwordLoading ? <CircularProgress size={20} /> : 'Update Password'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Two-Factor Authentication Password Dialog */}
      <Dialog open={show2FAPasswordDialog} onClose={handleClose2FAPasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {twoFactorEnabled ? 'Disable Two-Factor Authentication' : 'Enable Two-Factor Authentication'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {twoFactorEnabled 
              ? 'Please enter your password to disable two-factor authentication.'
              : 'Please enter your password to enable two-factor authentication. You will receive a verification code via email when logging in.'}
          </Typography>
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={twoFactorPassword}
            onChange={e => setTwoFactorPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && twoFactorPassword && !twoFactorLoading) {
                e.preventDefault();
                if (twoFactorEnabled) {
                  handleDisable2FA();
                } else {
                  handleEnable2FA();
                }
              }
            }}
            autoFocus
          />
          {twoFactorError && <Alert severity="error" sx={{ mt: 2 }}>{twoFactorError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose2FAPasswordDialog} disabled={twoFactorLoading}>Cancel</Button>
          <Button 
            onClick={twoFactorEnabled ? handleDisable2FA : handleEnable2FA} 
            variant="contained" 
            disabled={twoFactorLoading || !twoFactorPassword}
            type="submit"
          >
            {twoFactorLoading ? <CircularProgress size={20} /> : (twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography color="error" fontWeight={600} mb={2}>Are you sure you want to delete your account? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={handleDeleteAccount} variant="contained" color="error" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Team Member Dialog */}
      <TeamInviteDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onInvite={handleInviteMember}
        loading={inviteLoading}
        error={teamError}
        success={inviteSuccess}
      />

      {/* Remove Team Member Confirmation Dialog */}
      <Dialog
        open={removeMemberDialog}
        onClose={handleRemoveMemberCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FaExclamationTriangle style={{ color: '#f59e0b' }} />
          Remove Team Member
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{memberToRemove?.firstName} {memberToRemove?.lastName}</strong> from your team?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography variant="body2" color="text.secondary" component="li">
              Remove their access to team resources
            </Typography>
            <Typography variant="body2" color="text.secondary" component="li">
              Revoke their team member permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" component="li">
              Remove them from team communications
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. You'll need to send a new invitation to add them back to the team.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleRemoveMemberCancel}
            disabled={removingMember}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveMemberConfirm}
            color="error"
            variant="contained"
            disabled={removingMember}
            startIcon={removingMember ? <CircularProgress size={16} /> : <FaTrash />}
          >
            {removingMember ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deletion Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={showDeletionOverlay}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#fff'
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Your account has been deleted
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 16, opacity: 0.9 }}>
            You will be redirected to the homepage shortly...
          </Typography>
        </Box>
      </Backdrop>

      {/* Password Success Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={showPasswordSuccessOverlay}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#fff'
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Password changed successfully
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 16, opacity: 0.9 }}>
            Your password has been updated
          </Typography>
        </Box>
      </Backdrop>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? This will immediately downgrade you to the Free plan, remove access to premium features, and stop future billing.
            <ul>
            </ul>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            color="error" 
            variant="contained"
            disabled={billingLoading}
          >
            {billingLoading ? <CircularProgress size={16} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slack Info Dialog */}
      <Dialog open={showSlackInfoDialog} onClose={() => setShowSlackInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Slack Integration Commands</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, fontSize: 14 }}>
            Use these commands in your Slack workspace to interact with Velox AI:
          </Typography>
          <Box sx={{ 
            background: '#f8f9fa', 
            borderRadius: 1, 
            p: 2, 
            border: '1px solid #e9ecef',
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
              Available Commands:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                <code style={{ 
                  background: '#e9ecef', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace'
                }}>
                  /velox-search [company]
                </code>
                <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                  - Search for documents related to a specific company
                </span>
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                <code style={{ 
                  background: '#e9ecef', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace'
                }}>
                  /velox-list [limit]
                </code>
                <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                  - List recent documents (optional limit parameter)
                </span>
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 0, fontSize: 13 }}>
                <code style={{ 
                  background: '#e9ecef', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace'
                }}>
                  /velox-view [doc-id] [type]
                </code>
                <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                  - View a specific document by ID and type
                </span>
              </Typography>
            </Box>
          </Box>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Make sure your Slack account is linked in the integration settings above to use these commands.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSlackInfoDialog(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Docs Info Dialog */}
      <Dialog open={showGoogleDocsInfoDialog} onClose={() => setShowGoogleDocsInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Google Docs Integration Features</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, fontSize: 14 }}>
            Connect your Google Docs account to seamlessly export and import documents:
          </Typography>
          <Box sx={{ 
            background: '#f8f9fa', 
            borderRadius: 1, 
            p: 2, 
            border: '1px solid #e9ecef',
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
              Available Features:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Export Account Briefs, Battle Cards, and Objection Handling to Google Docs
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Import Google Docs to Knowledge Hub for AI analysis
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Real-time collaboration with team members
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Automatic formatting and styling preservation
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 0, fontSize: 13 }}>
                Version history and document sharing capabilities
              </Typography>
            </Box>
          </Box>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Connect your Google account in the integration settings above to start using these features.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGoogleDocsInfoDialog(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* HubSpot Info Dialog */}
      <Dialog open={showHubspotInfoDialog} onClose={() => setShowHubspotInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>HubSpot CRM Integration Features</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, fontSize: 14 }}>
            Connect your HubSpot CRM to import and sync customer data:
          </Typography>
          <Box sx={{ 
            background: '#f8f9fa', 
            borderRadius: 1, 
            p: 2, 
            border: '1px solid #e9ecef',
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
              Available Features:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Import contacts, companies, and deals from HubSpot
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Sync CRM data for enhanced account management
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Two-way data synchronization between platforms
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 0, fontSize: 13 }}>
                CRM data enrichment and management tools
              </Typography>
            </Box>
          </Box>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Connect your HubSpot account in the integration settings above to start syncing your CRM data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHubspotInfoDialog(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Salesforce Info Dialog */}
      <Dialog open={showSalesforceInfoDialog} onClose={() => setShowSalesforceInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Salesforce CRM Integration Features</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, fontSize: 14 }}>
            Connect your Salesforce CRM to import and sync customer data:
          </Typography>
          <Box sx={{ 
            background: '#f8f9fa', 
            borderRadius: 1, 
            p: 2, 
            border: '1px solid #e9ecef',
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
              Available Features:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Import accounts, contacts, and opportunities from Salesforce
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Sync CRM data for enhanced account management
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 1, fontSize: 13 }}>
                Two-way data synchronization between platforms
              </Typography>
              <Typography variant="body2" component="li" sx={{ mb: 0, fontSize: 13 }}>
                CRM data enrichment and management tools
              </Typography>
            </Box>
          </Box>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Connect your Salesforce account in the integration settings above to start syncing your CRM data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSalesforceInfoDialog(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}